/**
 * branchDataService — sube verisi icin SQL-only adaptor (Faz 4 sonrasi).
 *
 * OKUMA: `getBranches` yalniz SQL (`GET /api/Branch`, companyId X-Company-Id header'inda) okur.
 *        Firebase okuma-yedegi Faz 4'te kaldirildi.
 * YAZMA: create/update/delete + firma-bilgisi DAIMA dogrudan SQL'e yazar.
 *
 * KRITIK: subenin kanonik kimligi Firebase RTDB key'idir (= SQL Branch.FirebaseId).
 *         Tum sistem (personnel.branchesId, screen.branchId, ...) bu id'yi referans alir.
 *         Bu yuzden map'lerde `id = firebaseId` (SQL integer Id DEGIL).
 */

import { mainApi } from '../../config/apiClient';

export interface Branch {
  /** Kanonik sube kimligi (= Firebase RTDB key = SQL Branch.FirebaseId). */
  id: string;
  /** SQL integer primary key (yalnizca bilgi amacli — kanonik kimlik DEGIL). */
  sqlId?: number;
  name: string;
  address?: string;
  district?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  openingDate?: any;
  workingHours?: string;
  isOpen24Hours?: boolean;
  isHeadquarter?: boolean;
  isActive?: boolean;
  isFranchise?: boolean;
  // Franchise firma bilgisi (Firm*) — opsiyonel.
  firmName?: string;
  firmTaxNumber?: string;
  firmTaxOffice?: string;
  firmAddress?: string;
  firmPhone?: string;
  firmEmail?: string;
  firmWebsite?: string;
  firmMersisNo?: string;
  firmTradeRegistryNo?: string;
  /** Şube/franchise logosu (URL veya data:image;base64) — e-fatura XSLT logosu. */
  firmLogoUrl?: string;
  /** Şube/franchise kaşe/imza görseli — e-fatura XSLT __KASE_IMZA__ alanı. */
  firmStampUrl?: string;
  /** Firma bilgisi alt-nesnesi (Firebase uyumu icin sentezlenir / spread edilir). */
  companyInfo?: Record<string, any>;
  imageUrl?: string;
  mapUrl?: string;
  displayOrder?: number;
  showOnEcommerce?: boolean;
  // Firebase'den gelen ek alanlarin korunmasi icin passthrough.
  [key: string]: any;
}

export interface BranchWriteInput {
  name?: string;
  address?: string;
  district?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  openingDate?: string | Date | null;
  workingHours?: string;
  isOpen24Hours?: boolean;
  isFranchise?: boolean;
}

export interface BranchFirmInfo {
  firmName?: string;
  firmTaxNumber?: string;
  firmTaxOffice?: string;
  firmAddress?: string;
  firmPhone?: string;
  firmEmail?: string;
  firmWebsite?: string;
  firmMersisNo?: string;
  firmTradeRegistryNo?: string;
  /** Şube/franchise logosu. undefined → değiştirme, '' → temizle (backend kuralı). */
  firmLogoUrl?: string;
  /** Şube/franchise kaşe/imza görseli. undefined → değiştirme, '' → temizle. */
  firmStampUrl?: string;
  isFranchise?: boolean;
}

function mapSqlBranch(b: any): Branch {
  // Firma bilgisi alt-nesnesini Firm* kolonlarindan sentezle (Firebase companyInfo uyumu).
  const companyInfo: Record<string, any> = {
    firmName: b?.firmName ?? undefined,
    firmTaxNumber: b?.firmTaxNumber ?? undefined,
    firmTaxOffice: b?.firmTaxOffice ?? undefined,
    firmAddress: b?.firmAddress ?? undefined,
    firmPhone: b?.firmPhone ?? undefined,
    firmEmail: b?.firmEmail ?? undefined,
    firmWebsite: b?.firmWebsite ?? undefined,
    firmMersisNo: b?.firmMersisNo ?? undefined,
    firmTradeRegistryNo: b?.firmTradeRegistryNo ?? undefined,
  };
  return {
    ...b,
    // KRITIK: kanonik id = firebaseId (SQL integer Id DEGIL).
    id: String(b?.firebaseId ?? b?.id ?? ''),
    sqlId: typeof b?.id === 'number' ? b.id : undefined,
    name: b?.name || 'İsimsiz Şube',
    // SQL'de isHeadquarter kolonu yok → false. HybridFallback fallback'inde Firebase'den gelir.
    isHeadquarter: false,
    isActive: b?.isActive ?? true,
    isFranchise: b?.isFranchise ?? false,
    companyInfo,
  };
}

/**
 * Sirketin subelerini getirir (SADECE SQL — GET /api/Branch).
 * NOT: "Subeye Ait Degil" gibi UI yardimci ogeleri BURADA EKLENMEZ (cagiran taraf ekler) —
 *      boylece firebaseDataService.getBranches reroute'unda vardiya/gorev listeleri bozulmaz.
 */
export async function getBranches(_companyId: string): Promise<Branch[]> {
  const res = await mainApi.get('/api/Branch');
  const raw = Array.isArray(res.data?.branches) ? res.data.branches : [];
  return raw.map(mapSqlBranch);
}

/**
 * Tek bir subeyi kanonik id (firebaseId) ile getirir (SADECE SQL).
 * Eski `getCompanyData(companyId, 'branches/{id}')` karsiligi.
 */
export async function getBranchById(companyId: string, branchId: string): Promise<Branch | null> {
  const branches = await getBranches(companyId);
  return branches.find((b) => b.id === branchId) ?? null;
}

function toIsoOrNull(d: string | Date | null | undefined): string | null {
  if (!d) return null;
  if (d instanceof Date) return d.toISOString();
  return String(d);
}

/** Yeni sube olusturur (SADECE SQL). Donus: yeni sube id (firebaseId). */
export async function createBranch(data: BranchWriteInput & { name: string }): Promise<string> {
  const res = await mainApi.post('/api/Branch/create', {
    name: data.name,
    address: data.address,
    district: data.district,
    city: data.city,
    postalCode: data.postalCode,
    country: data.country,
    phone: data.phone,
    email: data.email,
    website: data.website,
    openingDate: toIsoOrNull(data.openingDate),
    workingHours: data.workingHours,
    isOpen24Hours: data.isOpen24Hours,
    isFranchise: data.isFranchise,
  });
  const branchId = res?.data?.branchId ?? res?.data?.firebaseId;
  if (!branchId) throw new Error('createBranch: sunucu branchId dondurmedi');
  return String(branchId);
}

/** Sube genel bilgilerini gunceller (SADECE SQL). Firm* alanlarina dokunmaz. */
export async function updateBranch(branchId: string, data: BranchWriteInput): Promise<void> {
  await mainApi.post('/api/Branch/update', {
    firebaseId: branchId,
    name: data.name,
    address: data.address,
    district: data.district,
    city: data.city,
    postalCode: data.postalCode,
    country: data.country,
    phone: data.phone,
    email: data.email,
    website: data.website,
    openingDate: toIsoOrNull(data.openingDate),
    workingHours: data.workingHours,
    isOpen24Hours: data.isOpen24Hours,
    isFranchise: data.isFranchise,
  });
}

/** Sube siler (SADECE SQL — varsayilan soft delete). */
export async function deleteBranch(branchId: string, hardDelete = false): Promise<void> {
  try {
    await mainApi.post('/api/Branch/delete', { firebaseId: branchId, hardDelete });
  } catch (error: any) {
    // Sunucu kalici silmeyi kullanim nedeniyle reddettiginde (409) hangi alanlarin
    // engel oldugu cagirana tasinir; istemci denetimi ile yaris durumu olusursa
    // kullanici gerekcesiz bir hata yerine listeyi gorur.
    const status = error?.response?.status;
    const payload = error?.response?.data;
    if (status === 409 && Array.isArray(payload?.usages)) {
      throw new BranchInUseError(payload.usages as BranchUsageItem[]);
    }
    throw error;
  }
}

/** Subenin kullanildigi tek bir alan. */
export interface BranchUsageItem {
  /** Alan anahtari (i18n icin) — ornek: `quickSaleScreens`. */
  key: string;
  /** Sunucudan gelen Turkce etiket (i18n anahtari yoksa yedek metin). */
  label: string;
  /** Yapilandirma tablolarinda kayit adedi; hareket tablolarinda `null`. */
  count?: number | null;
}

export interface BranchUsageResult {
  branchId: string;
  branchName: string;
  inUse: boolean;
  usages: BranchUsageItem[];
}

/** Kalici silme reddedildiginde firlatilir; engelleyen alanlari tasir. */
export class BranchInUseError extends Error {
  readonly usages: BranchUsageItem[];

  constructor(usages: BranchUsageItem[]) {
    super('Sube kullanimda oldugu icin silinemez');
    this.name = 'BranchInUseError';
    this.usages = usages;
  }
}

/**
 * Subenin sistemde kullanilip kullanilmadigini denetler (satis ekranlari, kasa/banka,
 * personel, stok, gecmis hareketler...). Kalici silme oncesi zorunlu adimdir.
 *
 * Sube anahtari query string'de gonderilir: yol segmentinde tasinmasi
 * TenantResolutionMiddleware'in yol tahminine takilabiliyor.
 */
export async function getBranchUsage(branchId: string): Promise<BranchUsageResult> {
  const response = await mainApi.get('/api/Branch/usage', { params: { firebaseId: branchId } });
  const data = response.data || {};
  const usages: BranchUsageItem[] = Array.isArray(data.usages) ? data.usages : [];
  return {
    branchId: data.branchId || branchId,
    branchName: data.branchName || '',
    inUse: usages.length > 0,
    usages,
  };
}

/**
 * Sube franchise/firma bilgisini getirir (SADECE SQL).
 * Firma alani Branch satirinin Firm* kolonlarindan gelir.
 */
export async function getBranchFirmInfo(companyId: string, branchId: string): Promise<BranchFirmInfo | null> {
  const branches = await getBranches(companyId);
  const found = branches.find((b) => b.id === branchId);
  if (!found) return null;
  return {
    firmName: found.firmName,
    firmTaxNumber: found.firmTaxNumber,
    firmTaxOffice: found.firmTaxOffice,
    firmAddress: found.firmAddress,
    firmPhone: found.firmPhone,
    firmEmail: found.firmEmail,
    firmWebsite: found.firmWebsite,
    firmMersisNo: found.firmMersisNo,
    firmTradeRegistryNo: found.firmTradeRegistryNo,
    firmLogoUrl: found.firmLogoUrl,
    firmStampUrl: found.firmStampUrl,
    isFranchise: found.isFranchise,
  };
}

/** Sube firma bilgisini gunceller (SADECE SQL — POST /api/branch/update-firm-details). companyId body'de zorunlu. */
export async function updateBranchFirmInfo(
  companyId: string,
  branchId: string,
  firm: BranchFirmInfo
): Promise<void> {
  await mainApi.post('/api/branch/update-firm-details', {
    companyId,
    branchFirebaseId: branchId,
    firmName: firm.firmName,
    firmTaxNumber: firm.firmTaxNumber,
    firmTaxOffice: firm.firmTaxOffice,
    firmAddress: firm.firmAddress,
    firmPhone: firm.firmPhone,
    firmEmail: firm.firmEmail,
    firmWebsite: firm.firmWebsite,
    firmMersisNo: firm.firmMersisNo,
    firmTradeRegistryNo: firm.firmTradeRegistryNo,
    // undefined ise JSON'a girmez → backend logoya/kaşeye dokunmaz; '' → temizler.
    firmLogoUrl: firm.firmLogoUrl,
    firmStampUrl: firm.firmStampUrl,
  });
}
